/**
 * Extra Services - Cart Cleanup (No Reload)
 *
 * When a main product is removed from cart, its service items
 * are automatically removed too — without page reload.
 *
 * Uses Shopify Section Rendering API to refresh the cart UI.
 */

(function () {
  'use strict';

  class ExtraServicesCartCleanup {
    constructor() {
      this._isCleaningUp = false;
      this._patchFetch();
    }

    // ─── Intercept all cart API calls ─────────────────────────────
    // When the theme removes/updates a cart item via AJAX,
    // we piggyback on the response to check for orphaned services.

    _patchFetch() {
      const self = this;
      const originalFetch = window.fetch;

      window.fetch = function (...args) {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        const isCartChange = url.includes('/cart/change') ||
                             (url.includes('/cart/update') && !self._isCleaningUp);

        const result = originalFetch.apply(this, args);

        if (isCartChange) {
          result.then(response => {
            // Clone so we don't consume the body the theme needs
            return response.clone().json();
          }).then(cart => {
            self._checkAndCleanOrphans(cart);
          }).catch(() => {});
        }

        return result;
      };
    }

    // ─── Check for orphaned services ──────────────────────────────

    async _checkAndCleanOrphans(cart) {
      if (this._isCleaningUp) return;
      if (!cart?.items?.length) return;

      // Collect all main product variant IDs (items WITHOUT _service_for)
      const mainIds = new Set();
      for (const item of cart.items) {
        if (!item.properties?.['_service_for']) {
          mainIds.add(String(item.variant_id));
          mainIds.add(String(item.id));
        }
      }

      // Find orphaned service items (parent no longer in cart)
      const orphanKeys = [];
      for (const item of cart.items) {
        if (item.properties?.['_service_for']) {
          if (!mainIds.has(String(item.properties['_service_for']))) {
            orphanKeys.push(item.key);
          }
        }
      }

      if (orphanKeys.length === 0) return;

      // Remove orphans
      this._isCleaningUp = true;
      try {
        const updates = {};
        orphanKeys.forEach(key => { updates[key] = 0; });

        const resp = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });

        if (resp.ok) {
          const updatedCart = await resp.json();
          // Refresh the cart UI without page reload
          await this._refreshCartUI(updatedCart);
        }
      } catch (e) {
        console.error('[Extra Services Cart] Cleanup error:', e);
      }
      this._isCleaningUp = false;
    }

    // ─── Refresh cart UI without reload ───────────────────────────

    async _refreshCartUI(cart) {
      // Method 1: Shopify Section Rendering API
      // Fetch the updated cart section HTML and swap it in
      const sectionRefreshed = await this._refreshViaSection();
      if (sectionRefreshed) {
        this._updateCartCount(cart);
        return;
      }

      // Method 2: Dispatch events the theme listens for
      this._dispatchCartEvents(cart);

      // Method 3: Fallback — reload only if nothing else worked
      // (should rarely reach here)
    }

    async _refreshViaSection() {
      try {
        // Find the main cart section on the page
        const cartSection = document.querySelector('[data-section-type="cart"]') ||
                            document.querySelector('[id^="shopify-section-"][id*="cart"]') ||
                            document.querySelector('main [data-section-id]');

        if (!cartSection) return false;

        // Get the section ID
        const sectionId = cartSection.dataset?.sectionId ||
                          cartSection.id?.replace('shopify-section-', '') ||
                          cartSection.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '');

        if (!sectionId) return false;

        // Fetch updated section HTML from Shopify
        const resp = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
        if (!resp.ok) return false;

        const html = await resp.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSection = doc.querySelector('[data-section-type="cart"]') ||
                           doc.querySelector('[id^="shopify-section-"]') ||
                           doc.body.firstElementChild;

        if (!newSection) return false;

        // Swap in the new HTML
        const wrapper = cartSection.closest('[id^="shopify-section-"]') || cartSection;
        const newWrapper = doc.querySelector(`#${wrapper.id}`) || newSection;
        wrapper.innerHTML = newWrapper.innerHTML;

        return true;
      } catch (e) {
        return false;
      }
    }

    _dispatchCartEvents(cart) {
      // Try common theme cart refresh events
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart } }));
      document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart } }));

      // For themes that re-render on popstate or custom events
      if (window.Shopify?.theme?.cart) {
        window.Shopify.theme.cart = cart;
      }
    }

    _updateCartCount(cart) {
      // Update header cart count badges
      const countEls = document.querySelectorAll(
        '.cart-count, .cart-count-bubble, [data-cart-count], .cart__count, .header__cart-count'
      );
      countEls.forEach(el => {
        el.textContent = cart.item_count;
        if (cart.item_count === 0) {
          el.style.display = 'none';
        }
      });
    }
  }

  // ─── Clean URL hash on cart page ─────────────────────────────
  // Shopify themes sometimes append a hash like #72ec9e... to the cart URL.
  // Remove it for a clean URL.

  function cleanCartHash() {
    if (window.location.pathname === '/cart' && window.location.hash) {
      history.replaceState(null, '', '/cart');
    }
  }

  // ─── Init ──────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      cleanCartHash();
      new ExtraServicesCartCleanup();
    });
  } else {
    cleanCartHash();
    new ExtraServicesCartCleanup();
  }
})();
